import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useQuery} from "@tanstack/react-query";
import {AgGridReact} from "ag-grid-react";
import {FaEraser, FaExclamationTriangle, FaPlus, FaSearch} from "react-icons/fa";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import {MaskDefault} from "../../../utils/mask.js";
import aggridUtils from "../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import {useFormNavigation} from "../../../hooks/use-form-navigation.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import projectUtils from "../../../utils/project-utils.js";
import IndividualMonitoringService from "../../../services/web/individual-monitoring-service.js";
import {clearCpf, getAttachUrl, USER_IMAGE} from "./utils/individual-monitoring-utils.js";
import Alert from "../../../components/alert/alert.jsx";
import SentryRow from "../../../components/template/sentry-row.jsx";

const initialFilter = {
    name: "",
    cpf: "",
};

const pathModel = {
    create: {dev: `/individual-monitoring/form`,  isBlank: false},
    edit: {dev: `/individual-monitoring/form`,  isBlank: true},
};

const ImageCell = ({value}) => (
    <div
        className={"img-thumbnail"}
        style={{
            backgroundImage: `url(${value ? getAttachUrl(value) : USER_IMAGE})`,
            backgroundPosition: "50% center",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            height: "50px",
        }}
    />
);

const IndividualMonitoringList = () => {
    const [filter, setFilter] = useState(initialFilter);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);

    const {onRowClicked, openForm} = useFormNavigation(pathModel);

    const {data: status} = useQuery({
        queryFn: () => IndividualMonitoringService.status(),
        queryKey: ["facial-recognition-status"]
    });

    const {data = []} = useQuery({
        queryFn: () => IndividualMonitoringService.list(),
        queryKey: ["individual-monitoring"]
    });

    const columnDefs = useMemo(() => [
        {field: 'id', headerName: "ID", hide: true},
        {field: 'image_id', headerName: "Foto", width: 90, headerSort: false, sortable: false, filter: false, cellRenderer: ImageCell},
        {field: 'name', headerName: "Nome", flex: 1, filter: 'agTextColumnFilter',
            comparator: (a, b, nodeA, nodeB, isInverted) => aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted)},
        {field: 'cpf', headerName: "CPF", flex: 1, filter: 'agTextColumnFilter',
            valueFormatter: params => projectUtils.formatCpfToView(params.value)},
        {field: 'dt_birth', headerName: "Data de nascimento", flex: 1,
            valueFormatter: params => params.value ? new Date(params.value + " 00:00:00").toLocaleDateString('pt-BR') : ""},
        {field: 'watchlists', headerName: "Motivo de monitoramento", flex: 1, filter: 'agTextColumnFilter'},
    ], []);

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        if (filter.name) filterModel.name = {filterType: "text", type: "contains", filter: filter.name};
        if (filter.cpf) filterModel.cpf = {filterType: "text", type: "contains", filter: clearCpf(filter.cpf)};

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    const handleInput = ({target: {id, value}}) => setFilter(prev => ({...prev, [id]: value}));

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    return (
        <Template>
            <Template.Header>
                <Template.Title browserTitle={'Monitoramentos'}>Monitoramentos</Template.Title>
                <Button onClick={openForm}><FaPlus/>Adicionar monitoramento</Button>
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <InputForm
                                id={"name"}
                                label={"Nome"}
                                value={filter.name}
                                onChange={handleInput}
                                classNameWrapper={"col-span-6"}
                            />

                            <div className={"flex justify-between col-span-6 gap-4"}>
                                <InputForm
                                    id={"cpf"}
                                    label={"CPF"}
                                    value={filter.cpf}
                                    onChange={handleInput}
                                    onBlur={handleInput}
                                    mask={MaskDefault.cpf}
                                    classNameWrapper={"w-full"}
                                />

                                <div className={"flex gap-2 justify-end"}>
                                    <Button onClick={clearFilter} title={"Limpar"} target={"primary-outline"}><FaEraser/></Button>
                                    <Button onClick={search} title={"Buscar"}><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>

                {
                    status === false &&
                    <Alert target={"danger"} className={"mt-2 flex items-center gap-2"}>
                        <FaExclamationTriangle /> Sistema de reconhecimento facial não configurado.
                    </Alert>
                }

                <Card className={"mt-2"}>
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                            <AgGridReact
                                onGridReady={(params) => setGridApi(params.api)}
                                ref={gridRef}
                                rowData={data}
                                columnDefs={columnDefs}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                pagination
                                onRowClicked={onRowClicked}
                                paginationPageSize={100}
                                paginationPageSizeSelector={[10, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                rowHeight={60}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default IndividualMonitoringList;
